/* Patient Dashboard — iter98.11 redesign.
 *
 * Layout (translated from the user-supplied HTML mockup, palette retained
 * as the existing Apollo cyan-teal `primary` token instead of the mockup's
 * emerald green):
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  Hero — "Your next step" (situational by case phase)    │
 *   ├──────────────────────────┬──────────────────────────────┤
 *   │  Today's wear time       │  Next switch / Retainer      │
 *   │  + 7-day mini chart      │  countdown                   │
 *   ├──────────────────────────┴──────────────────────────────┤
 *   │  Sunday Check-In (state-aware: sunday-active/done/missed)│
 *   ├─────────────────────────────────────────────────────────┤
 *   │  Smile journey strip — Started → Treatment → Retention  │
 *   ├─────────────────────────────────────────────────────────┤
 *   │  Dr. [Name]'s latest note                                │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Data sources (single round-trip per endpoint):
 *   /appointments/my             — for booking CTAs + upcoming chip
 *   /cases/mine                  — for activeCase + status driver
 *   /patient/me/aligner-progress — wear series, stage, next switch, nudges
 *   /patient/me/check-ins        — this_week_done + last check-in
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
// iter98.99f3 — Compliance coach banner (renders null when no active thread)
import { CoachBanner } from "@/pages/patient/CoachChat";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Calendar, Camera, CheckCircle, Clock, ArrowRight, Sparkle,
    ArrowsClockwise, ChatCircleDots, ClockUser, Trophy, CaretRight,
    Info, CalendarPlus, ImageSquare,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import TreatmentLockedCard from "@/components/patient/TreatmentLockedCard";
import DoctorAvatar from "@/components/common/DoctorAvatar";
import SmileGoalsOnboarding from "@/components/patient/SmileGoalsOnboarding";

// ────────────────────────────────────────────────────────────────────
// Tiny helpers
// ────────────────────────────────────────────────────────────────────
const fmtDate = (iso, opts = { day: "2-digit", month: "short" }) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", opts); }
    catch { return iso; }
};

const fmtRelative = (iso) => {
    if (!iso) return "—";
    try {
        const then = new Date(iso);
        const days = Math.floor((Date.now() - then.getTime()) / 86400000);
        if (days === 0) return "today";
        if (days === 1) return "yesterday";
        if (days < 7) return `${days} days ago`;
        if (days < 14) return "last week";
        return fmtDate(iso, { day: "2-digit", month: "short" });
    } catch { return ""; }
};

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
};

// ────────────────────────────────────────────────────────────────────
// WelcomeCompletionCard — just-in-time profile capture (unchanged
// from the previous dashboard; surfaces only on first portal load when
// the patient hasn't filled in their name yet).
// ────────────────────────────────────────────────────────────────────
function WelcomeCompletionCard() {
    const { user, setUser } = useAuth();
    const [name, setName] = useState("");
    const [email, setEmail] = useState(user?.email || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!name.trim()) { toast.error("Please tell us your name"); return; }
        setSaving(true);
        try {
            const { data } = await api.put("/auth/profile", { name: name.trim(), email: email.trim() || null });
            setUser(data);
            toast.success(`Welcome, ${name.trim().split(" ")[0]}!`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save your details");
        } finally { setSaving(false); }
    };

    return (
        <div
            className="mb-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-white to-amber-50/40 p-5 sm:p-6"
            data-testid="patient-welcome-completion"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center shrink-0">
                    <Sparkle size={18} weight="fill" className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">Welcome to Invisique™!</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Just a quick intro so we can personalise your portal.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3 mt-4">
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name *" className="h-10" data-testid="welcome-name-input" onKeyDown={(e) => e.key === "Enter" && save()} />
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" className="h-10" data-testid="welcome-email-input" onKeyDown={(e) => e.key === "Enter" && save()} />
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                        <Button onClick={save} disabled={saving || !name.trim()} className="rounded-full" data-testid="welcome-save-btn">
                            {saving ? "Saving…" : "Continue"}
                        </Button>
                        <span className="text-xs text-muted-foreground">You can update these anytime in your profile.</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Hero — "Your next step", situational copy by case phase.
// ────────────────────────────────────────────────────────────────────
function deriveHeroPhase(activeCase, progress) {
    if (!activeCase) return "no_case";
    if (!activeCase.treatment_activated_at) return "pre_treatment";
    if (["completed", "retention"].includes(activeCase.status)) return "complete";
    const stage = Number(activeCase.current_aligner_stage || 0);
    const total = Math.max(
        Number(activeCase.plan?.aligner_count_upper || 0),
        Number(activeCase.plan?.aligner_count_lower || 0),
        Number(progress?.stage?.total || 0),
    );
    if (total > 0 && stage >= total) return "final_aligner";
    return "in_treatment";
}

function HeroNextStep({ activeCase, progress }) {
    const phase = deriveHeroPhase(activeCase, progress);
    const startedAt = activeCase?.treatment_activated_at;
    const stage = Number(activeCase?.current_aligner_stage || 0);
    const total = Math.max(
        Number(activeCase?.plan?.aligner_count_upper || 0),
        Number(activeCase?.plan?.aligner_count_lower || 0),
        Number(progress?.stage?.total || 0),
    );

    const variants = {
        no_case: {
            label: "Getting started",
            title: "Welcome to your smile journey",
            sub: "Your doctor will open a case for you after your consultation.",
            pills: [{ icon: <Calendar size={13} />, text: "Awaiting consultation" }],
            cta: { label: "View appointments", to: "/patient/appointments", icon: <ArrowRight size={14} /> },
        },
        pre_treatment: {
            label: "Your next step",
            title: "Your aligners are on the way",
            sub: "Your case is in production. We'll unlock your treatment dashboard the day your doctor fits you in-chair.",
            pills: [
                { icon: <Clock size={13} />, text: "In production" },
                { icon: <Calendar size={13} />, text: "Book a fitting visit" },
            ],
            cta: { label: "Track case status", to: "/patient/my-case", icon: <ArrowRight size={14} /> },
        },
        in_treatment: {
            label: "Your next step",
            title: `Stay on track — Aligner ${stage} of ${total || "?"}`,
            sub: "Wear your aligners 22 hours a day and log today's wear so your doctor can see you're on plan.",
            pills: [
                { icon: <CheckCircle size={13} />, text: `${stage}/${total || "?"} aligners` },
                { icon: <Clock size={13} />, text: progress?.stage?.next_switch_in_days != null ? `Next switch in ${progress.stage.next_switch_in_days}d` : "Plan active" },
                ...(startedAt ? [{ icon: <Calendar size={13} />, text: `Started ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
            ],
            cta: { label: "View my case", to: "/patient/my-case", icon: <ArrowRight size={14} /> },
        },
        final_aligner: {
            label: "Almost there",
            title: "Final aligner — last stretch!",
            sub: "Give this last tray the full 22 hours every day. Once it's done your doctor will switch you to retention.",
            pills: [
                { icon: <Trophy size={13} weight="fill" />, text: `${stage}/${total} aligners` },
                { icon: <Clock size={13} />, text: progress?.stage?.next_switch_in_days != null ? `Wraps up in ${Math.max(0, progress.stage.next_switch_in_days)}d` : "Final tray" },
                ...(startedAt ? [{ icon: <Calendar size={13} />, text: `Started ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
            ],
            cta: { label: "View final-stretch tips", to: "/patient/my-case", icon: <ArrowRight size={14} /> },
        },
        complete: {
            label: "Your next step",
            title: "Treatment complete — time for retention!",
            sub: "You've finished all your aligners. Your smile is set — now let's keep it that way.",
            pills: [
                { icon: <Trophy size={13} weight="fill" />, text: `${total}/${total} aligners done` },
                { icon: <Calendar size={13} />, text: "Retention phase active" },
                ...(startedAt ? [{ icon: <Clock size={13} />, text: `Started ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
            ],
            cta: { label: "Book retention review", to: activeCase?.doctor_id ? `/book/${activeCase.doctor_id}` : "/patient/appointments", icon: <CalendarPlus size={14} /> },
        },
    };
    const v = variants[phase];

    return (
        <div
            className="relative overflow-hidden rounded-2xl p-6 sm:p-7 mb-3 text-white shadow-sm"
            style={{
                background: "linear-gradient(135deg, hsl(193 100% 22%) 0%, hsl(192 100% 31%) 55%, hsl(173 58% 42%) 100%)",
            }}
            data-testid={`hero-next-step-${phase}`}
        >
            {/* Decorative blobs (subtle, doesn't compete with copy) */}
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/[0.06]" />
            <div className="absolute bottom-[-50px] right-16 w-32 h-32 rounded-full bg-white/[0.04]" />

            <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-white/65 mb-2">{v.label}</div>
                <h2 className="font-display text-xl sm:text-2xl font-bold leading-tight">{v.title}</h2>
                <p className="text-sm text-white/80 mt-1.5 max-w-2xl">{v.sub}</p>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                    {v.pills.map((p, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-xs font-medium border border-white/15">
                            {p.icon}{p.text}
                        </span>
                    ))}
                </div>

                <div className="mt-5">
                    <Link to={v.cta.to}>
                        <Button
                            className="rounded-lg bg-white/15 hover:bg-white/25 text-white border border-white/25 backdrop-blur-sm h-10 px-4 font-medium"
                            data-testid="hero-cta-btn"
                        >
                            {v.cta.icon}<span className="ml-1.5">{v.cta.label}</span>
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Today's tasks — a 3-bullet "what should I do right now?" checklist
// that ticks off as the patient hits each item. Lives between the hero
// and the 2-col grid so the page has a clear daily-action anchor
// without competing with the hero CTA.
// ────────────────────────────────────────────────────────────────────
function TodayTasksStrip({ progress, checkIns }) {
    const wear = progress?.wear || {};
    const target = Number(wear.target_hours || 22);
    const today = wear.today_hours != null ? Number(wear.today_hours) : null;

    // Task semantics:
    //  • wearTarget — patient hit the 22h target today (counted as
    //    "close enough" at ≥20h since the spec accounts for eating /
    //    cleaning breaks).
    //  • logToday  — any log exists for today (regardless of hours).
    //  • checkIn   — this_week_done from /patient/me/check-ins.
    const wearTargetDone = today != null && today >= 20;
    const logTodayDone = today != null;
    const checkInDone = !!checkIns?.this_week_done;

    const tasks = [
        {
            id: "wear",
            label: "Wear your aligners 22 hours today",
            sub: today != null
                ? wearTargetDone ? `Crushed it — ${today.toFixed(1)}h` : `${today.toFixed(1)}h so far · ${(target - today).toFixed(1)}h to go`
                : "Aim for 22h to stay on plan",
            done: wearTargetDone,
        },
        {
            id: "checkin",
            label: "Submit this week's check-in",
            sub: checkInDone ? "Already in — your doctor will review" : "Upload a smile photo on Sunday",
            done: checkInDone,
        },
        {
            id: "log",
            label: "Log tonight's wear time",
            sub: logTodayDone ? "Logged ✓" : "Open the wear-log dialog to record today",
            done: logTodayDone,
        },
    ];

    const doneCount = tasks.filter((t) => t.done).length;
    const allDone = doneCount === tasks.length;

    return (
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 mb-3" data-testid="today-tasks-strip">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-[3px] h-4 bg-primary rounded-full" />
                    <h3 className="text-sm font-medium">Today&apos;s focus</h3>
                </div>
                <span
                    className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${
                        allDone ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"
                    }`}
                    data-testid="today-tasks-progress"
                >
                    {doneCount} / {tasks.length} done{allDone ? " 🎉" : ""}
                </span>
            </div>
            <ul className="space-y-2">
                {tasks.map((t) => (
                    <li
                        key={t.id}
                        className="flex items-start gap-3"
                        data-testid={`today-task-${t.id}-${t.done ? "done" : "todo"}`}
                    >
                        {t.done ? (
                            <CheckCircle size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
                        ) : (
                            <div className="w-4 h-4 rounded-full border-[1.5px] border-border shrink-0 mt-1" />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className={`text-sm leading-tight ${t.done ? "text-muted-foreground line-through" : ""}`}>
                                {t.label}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{t.sub}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Section header — small left-bar accent + title + optional link
// ────────────────────────────────────────────────────────────────────
function SectionHeader({ title, link, linkLabel }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
                <div className="w-[3px] h-4 bg-primary rounded-full" />
                <h3 className="text-sm font-medium">{title}</h3>
            </div>
            {link && (
                <Link to={link} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid={`section-link-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                    {linkLabel || "See more"} <ArrowRight size={13} />
                </Link>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Wear time card — big stat + horizontal bar + 7-day mini chart + log
// ────────────────────────────────────────────────────────────────────
function WearTimeCard({ progress, onReload }) {
    const [open, setOpen] = useState(false);

    const wear = progress?.wear || {};
    const target = Number(wear.target_hours || 22);
    const todayHours = wear.today_hours != null ? Number(wear.today_hours) : null;
    const avg7d = wear.avg_7d != null ? Number(wear.avg_7d) : null;
    const series7 = (wear.series || []).slice(-7);
    const pct = todayHours != null ? Math.min(100, Math.round((todayHours / target) * 100)) : 0;
    const pctClass = pct >= 90 ? "" : pct >= 65 ? "bg-amber-500" : "bg-rose-500";

    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="wear-time-card">
            <SectionHeader title="Today's wear time" />

            <div className="font-display font-bold text-[28px] leading-none mt-2">
                {todayHours != null ? todayHours.toFixed(1) : "—"}
                <span className="text-sm font-normal text-muted-foreground ml-1.5">/ {target} hrs</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
                {todayHours != null ? `${pct}% of daily target` : "No log yet for today"}
            </div>

            {/* Horizontal target bar */}
            <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all ${pctClass || "bg-primary"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="text-[10px] text-muted-foreground text-right mt-1">Target: {target} hrs</div>

            {/* 7-day mini chart */}
            <div className="flex items-end gap-1.5 h-12 mt-4">
                {Array.from({ length: 7 }).map((_, i) => {
                    const s = series7[i];
                    const hours = s?.hours || 0;
                    const ratio = Math.max(0.04, Math.min(1, hours / target));
                    const cls = !s?.logged
                        ? "bg-muted/60"
                        : hours >= 20 ? "bg-primary"
                            : hours >= 14 ? "bg-amber-500"
                                : "bg-rose-500";
                    const dayLabel = s ? new Date(s.date).toLocaleDateString("en-IN", { weekday: "narrow" }) : "—";
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div className="w-full bg-secondary/60 rounded-t-[3px] h-10 flex items-end overflow-hidden">
                                <div
                                    className={`w-full rounded-t-[2px] transition-all ${cls}`}
                                    style={{ height: `${ratio * 100}%` }}
                                    title={s ? `${dayLabel} · ${hours}h` : "No log"}
                                />
                            </div>
                            <div className="text-[9px] text-muted-foreground">{dayLabel}</div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between mt-1">
                <div className="text-xs">
                    {avg7d != null ? (
                        <>
                            <span className="font-medium">Avg {avg7d.toFixed(1)}h</span>
                            <span className="ml-1.5 text-primary text-[11px]">
                                {avg7d >= 20 ? "On track" : avg7d >= 17 ? "Almost there" : "Off plan"}
                            </span>
                        </>
                    ) : (
                        <span className="text-muted-foreground">Log some days to see your average</span>
                    )}
                </div>
                <div className="text-[10px] text-muted-foreground">7-day view</div>
            </div>

            <Button
                variant="outline"
                size="sm"
                className="mt-4 rounded-lg gap-1.5 text-xs h-8"
                onClick={() => setOpen(true)}
                data-testid="wear-log-open-btn"
            >
                <ClockUser size={14} /> Log today&apos;s time
            </Button>

            <WearLogDialog
                open={open}
                target={target}
                initialHours={todayHours != null ? Math.floor(todayHours) : 20}
                initialMins={todayHours != null ? Math.round((todayHours % 1) * 60) : 0}
                onClose={() => setOpen(false)}
                onSaved={() => { setOpen(false); onReload?.(); }}
            />
        </div>
    );
}

function WearLogDialog({ open, target, initialHours, initialMins, onClose, onSaved }) {
    // Treat the dialog opening as a "key change" — fresh inputs are
    // computed from props on each mount rather than synced through a
    // setState-in-effect (which trips react-hooks/set-state-in-effect).
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <WearLogDialogBody
                key={open ? `${initialHours}-${initialMins}` : "closed"}
                open={open}
                target={target}
                initialHours={initialHours}
                initialMins={initialMins}
                onClose={onClose}
                onSaved={onSaved}
            />
        </Dialog>
    );
}

function WearLogDialogBody({ open, target, initialHours, initialMins, onClose, onSaved }) {
    const [hours, setHours] = useState(String(initialHours));
    const [mins, setMins] = useState(String(initialMins));
    const [busy, setBusy] = useState(false);

    const h = Math.max(0, Math.min(24, Number(hours) || 0));
    const m = Math.max(0, Math.min(59, Number(mins) || 0));
    const total = h + m / 60;
    const diff = target - total;
    const preview = total >= target
        ? `🎉 That&rsquo;s ${h}h ${m}m — you&rsquo;ve hit your daily ${target}hr target!`.replace(/&rsquo;/g, "\u2019")
        : `That&rsquo;s ${h}h ${m}m — ${Math.floor(diff)}h ${Math.round((diff % 1) * 60)}m short of the ${target}hr target.`.replace(/&rsquo;/g, "\u2019");

    const submit = async () => {
        if (!(total >= 0 && total <= 24)) { toast.error("Enter a value between 0 and 24"); return; }
        setBusy(true);
        try {
            await api.post("/patient/me/wear-log", { hours: Number(total.toFixed(2)) });
            toast.success("Wear time saved");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save");
        } finally { setBusy(false); }
    };

    return (
        <DialogContent className="max-w-sm" data-testid="wear-log-dialog">
            <DialogHeader>
                <DialogTitle>Log today&apos;s wear time</DialogTitle>
                <DialogDescription>How long did you wear your aligners today?</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-3 mt-2">
                <div className="flex-1">
                    <label className="text-xs text-muted-foreground block mb-1.5">Hours</label>
                    <Input type="number" min="0" max="24" value={hours} onChange={(e) => setHours(e.target.value)} className="text-center text-xl font-semibold h-12" data-testid="wear-hours-input" />
                    <div className="text-[10px] text-muted-foreground text-center mt-1">hrs</div>
                </div>
                <div className="text-xl text-muted-foreground pt-5">:</div>
                <div className="flex-1">
                    <label className="text-xs text-muted-foreground block mb-1.5">Minutes</label>
                    <Input type="number" min="0" max="59" value={mins} onChange={(e) => setMins(e.target.value)} className="text-center text-xl font-semibold h-12" data-testid="wear-mins-input" />
                    <div className="text-[10px] text-muted-foreground text-center mt-1">min</div>
                </div>
            </div>
            <div className="text-xs text-muted-foreground flex items-start gap-1.5 mt-1">
                <Info size={14} className="shrink-0 mt-0.5" />
                <span>{preview}</span>
            </div>
            <DialogFooter className="mt-2">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={submit} disabled={busy} data-testid="wear-save-btn">
                    {busy ? "Saving…" : "Save wear time"}
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}

// ────────────────────────────────────────────────────────────────────
// Next switch / Retainer reminder card.
// ────────────────────────────────────────────────────────────────────
function NextSwitchCard({ progress, activeCase }) {
    const phase = deriveHeroPhase(activeCase, progress);
    const stage = progress?.stage || {};
    const days = stage.next_switch_in_days != null ? Math.max(0, stage.next_switch_in_days) : null;
    const total = Number(activeCase?.plan?.aligner_count_upper || stage.total || 0);

    if (phase === "complete") {
        return (
            <div className="bg-card border border-border rounded-2xl p-5" data-testid="next-switch-card-retention">
                <SectionHeader title="Retainer reminder" />
                <div className="font-display font-bold text-[40px] leading-none text-primary mt-2">
                    {days != null ? days : "—"}
                    <span className="text-sm font-normal text-muted-foreground ml-1.5">days</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5">Until your next check-in with your doctor</div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mt-3">
                    <ArrowsClockwise size={13} /> Aligner {total} → Retainer
                </div>
                <div className="border-t border-border mt-4 pt-3">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Retention protocol</div>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                        Full time for 3 months<br />
                        Nights only thereafter<br />
                        Recall: 6 months
                    </div>
                </div>
                <Link to={activeCase?.doctor_id ? `/book/${activeCase.doctor_id}` : "/patient/appointments"}>
                    <Button variant="outline" size="sm" className="mt-4 rounded-lg gap-1.5 text-xs h-8 w-full">
                        <Calendar size={14} /> Book recall appointment
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="next-switch-card">
            <SectionHeader title={phase === "final_aligner" ? "Wrap-up" : "Next aligner switch"} />
            <div className="font-display font-bold text-[40px] leading-none text-primary mt-2">
                {days != null ? days : "—"}
                <span className="text-sm font-normal text-muted-foreground ml-1.5">days</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">
                {phase === "final_aligner"
                    ? "Until your aligner journey wraps up"
                    : stage.next_switch_date
                        ? `Switch on ${fmtDate(stage.next_switch_date, { day: "2-digit", month: "short" })}`
                        : "Until you switch trays"}
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mt-3">
                <ArrowsClockwise size={13} />
                {phase === "final_aligner"
                    ? `Aligner ${stage.current || total} → Retainer`
                    : `Aligner ${stage.current || "—"} → ${stage.current ? stage.current + 1 : "—"}`}
            </div>

            <div className="border-t border-border mt-4 pt-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Tray cadence</div>
                <div className="text-xs leading-relaxed text-muted-foreground">
                    {stage.cadence_days || 7} days per tray
                    {stage.day_of_tray ? ` · Day ${stage.day_of_tray} of this tray` : ""}<br />
                    Total: {total || "—"} aligners
                </div>
            </div>

            <Link to="/patient/my-case">
                <Button variant="outline" size="sm" className="mt-4 rounded-lg gap-1.5 text-xs h-8 w-full">
                    <ImageSquare size={14} /> View wear schedule
                </Button>
            </Link>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Sunday Check-In card — state-aware.
// ────────────────────────────────────────────────────────────────────
function SundayCheckInCard({ activeCase, checkIns, onReload }) {
    const fileInputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const last = checkIns?.check_ins?.[0];
    const thisWeekDone = !!checkIns?.this_week_done;
    // Captured once on mount via lazy initializer so render stays pure
    // (the value is only used for human-scale "is it Sunday today?" /
    // "how many days since…" comparisons — refreshing on every render
    // would be wasteful anyway).
    const [nowMs] = useState(() => Date.now());
    const isSunday = useMemo(() => new Date(nowMs).getDay() === 0, [nowMs]);
    const daysSinceLast = useMemo(() => {
        if (!last?.created_at) return null;
        return Math.floor((nowMs - new Date(last.created_at).getTime()) / 86400000);
    }, [last?.created_at, nowMs]);
    // Missed = no check-in this week AND last check-in (if any) was > 7 days ago
    const missed = !thisWeekDone && daysSinceLast != null && daysSinceLast > 7;

    const state = thisWeekDone ? "done" : isSunday ? "sunday-active" : missed ? "missed" : "default";

    const variants = {
        "default":        { pill: "Next check-in: Sunday",   pillCls: "bg-secondary text-muted-foreground", cardCls: "" },
        "sunday-active":  { pill: "📅 Due today — Sunday!",  pillCls: "bg-primary/10 text-primary",          cardCls: "ring-2 ring-primary/10 border-primary/40" },
        "done":           { pill: "✅ Submitted this week",  pillCls: "bg-primary/10 text-primary",          cardCls: "border-primary/40 bg-primary/[0.04]" },
        "missed":         { pill: "⚠️ Missed last Sunday",  pillCls: "bg-amber-100 text-amber-800",         cardCls: "ring-2 ring-amber-200 border-amber-300" },
    };
    const v = variants[state];

    const descCopy = {
        "default":       "Upload your weekly smile photos every Sunday so your doctor can track your progress remotely. It takes 2 minutes.",
        "sunday-active": "Today is check-in day! Upload your smile photos now so your doctor can review your progress this week.",
        "done":          "",
        "missed":        "You missed last week's check-in. No worries — upload your photos now so your doctor stays up to date.",
    }[state];

    const handlePickFile = () => fileInputRef.current?.click();

    const onFileChosen = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-pick of same file
        if (!file) return;
        if (!activeCase) { toast.error("No active case yet"); return; }
        setBusy(true);
        try {
            // 1. Create a check-in row for this week
            const { data: checkIn } = await api.post("/patient/me/check-ins", {
                feels_good: null,
                note: "",
            });
            // 2. Attach photo
            const fd = new FormData();
            fd.append("photo", file);
            await api.post(`/patient/me/check-ins/${checkIn.id}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Check-in submitted ✅");
            onReload?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't upload your photo");
        } finally { setBusy(false); }
    };

    return (
        <div className={`bg-card border border-border rounded-2xl p-5 transition-all mb-3 ${v.cardCls}`} data-testid={`checkin-card-${state}`}>
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Camera size={18} className="text-primary" weight="duotone" />
                    <h3 className="text-sm font-medium">Weekly Check-In</h3>
                    {state === "sunday-active" && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
                </div>
                <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${v.pillCls}`} data-testid="checkin-state-pill">
                    {v.pill}
                </span>
            </div>

            {descCopy && <div className="text-xs text-muted-foreground leading-relaxed mb-3">{descCopy}</div>}

            {state === "done" ? (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/[0.06]">
                    <CheckCircle size={20} className="text-primary shrink-0" weight="fill" />
                    <div>
                        <div className="text-sm font-medium text-primary">Photo submitted ✅</div>
                        <div className="text-xs text-primary/70">Your doctor will review shortly. See you next Sunday!</div>
                    </div>
                </div>
            ) : (
                <>
                    <button
                        onClick={handlePickFile}
                        disabled={busy || !activeCase}
                        className="w-full border border-dashed border-border rounded-lg p-5 text-center hover:border-primary/50 hover:bg-primary/[0.03] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="checkin-upload-btn"
                    >
                        <Camera size={22} className="mx-auto text-muted-foreground mb-1.5" />
                        <div className="text-sm text-foreground/80">
                            {busy ? "Uploading…" : "Tap to upload your smile photo"}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">Front · JPG or PNG · Max 5 MB</div>
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={onFileChosen}
                        className="hidden"
                        data-testid="checkin-file-input"
                    />
                </>
            )}

            {last && state !== "done" && (
                <div className="text-[11px] text-muted-foreground mt-2.5 flex items-center gap-1">
                    <Clock size={13} /> Last uploaded {fmtRelative(last.created_at)}
                    {last.week_of ? ` (${last.week_of})` : ""}
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Smile journey strip — Started → Treatment → Retention → Complete
// ────────────────────────────────────────────────────────────────────
function SmileJourneyStrip({ activeCase, progress, checkIns }) {
    // Hooks first — `nowMs` captured once on mount keeps the
    // weeks-active derivation pure across rerenders.
    const [nowMs] = useState(() => Date.now());
    const weeksActive = useMemo(() => {
        if (!activeCase?.treatment_activated_at) return 0;
        return Math.max(1, Math.floor((nowMs - new Date(activeCase.treatment_activated_at).getTime()) / (7 * 86400000)));
    }, [activeCase?.treatment_activated_at, nowMs]);

    const phase = deriveHeroPhase(activeCase, progress);
    const total = Number(activeCase?.plan?.aligner_count_upper || progress?.stage?.total || 0);
    const stage = Number(activeCase?.current_aligner_stage || progress?.stage?.current || 0);

    // Phase 0=Started · 1=Treatment · 2=Retention · 3=Complete
    const activeIdx = ({
        no_case: 0,
        pre_treatment: 0,
        in_treatment: 1,
        final_aligner: 1,
        complete: 2,
    })[phase] ?? 0;

    const phases = useMemo(() => ([
        {
            key: "started",
            label: "Started",
            sub: activeCase?.created_at ? fmtDate(activeCase.created_at, { month: "short", year: "numeric" }) : "Pending",
        },
        {
            key: "treatment",
            label: "Treatment",
            sub: total ? (stage >= total ? `✓ ${total}/${total} done` : `${stage}/${total} aligners`) : "Plan pending",
        },
        {
            key: "retention",
            label: "Retention",
            sub: activeIdx === 2 ? "← You are here" : activeIdx > 2 ? "Done" : "Up next",
        },
        {
            key: "complete",
            label: "Complete",
            sub: activeIdx >= 3 ? "✓" : "~ Soon",
        },
    ]), [activeCase?.created_at, total, stage, activeIdx]);

    // How "full" each connecting line is (0..1).
    const lineProgress = (i) => {
        if (i < activeIdx) return 1;
        if (i > activeIdx) return 0;
        // Currently-active phase — partial fill based on intra-phase progress
        if (phase === "in_treatment" && total > 0) return Math.min(1, stage / total);
        if (phase === "final_aligner") return 0.95;
        if (phase === "pre_treatment") return 0.4;
        return 0.5;
    };

    const checkInCount = checkIns?.check_ins?.length || 0;

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-3" data-testid="smile-journey-strip">
            <SectionHeader title="Your smile journey" link="/patient/my-case" linkLabel="Full history" />

            <div className="px-2 sm:px-4 pt-1">
                <div className="flex items-center">
                    {phases.map((p, i) => {
                        const done = i < activeIdx;
                        const active = i === activeIdx;
                        return (
                            <React.Fragment key={p.key}>
                                <div className="flex flex-col items-center gap-1.5" style={{ width: i === 0 || i === phases.length - 1 ? 64 : 80 }}>
                                    {done ? (
                                        <div className="w-3.5 h-3.5 rounded-full bg-primary border-2 border-primary grid place-items-center text-white">
                                            <span className="text-[7px] leading-none">✓</span>
                                        </div>
                                    ) : active ? (
                                        <div className="w-3.5 h-3.5 rounded-full bg-card border-2 border-primary" style={{ boxShadow: "0 0 0 4px hsla(192,100%,31%,0.15)" }} />
                                    ) : (
                                        <div className="w-3.5 h-3.5 rounded-full bg-card border-2 border-border" />
                                    )}
                                    <div className={`text-[11px] ${done ? "text-primary font-medium" : active ? "font-semibold" : "text-muted-foreground"} text-center`}>
                                        {p.label}
                                    </div>
                                    <div className={`text-[10px] text-center ${active ? "text-primary font-medium" : "text-muted-foreground"}`}>
                                        {p.sub}
                                    </div>
                                </div>
                                {i < phases.length - 1 && (
                                    <div className="flex-1 h-0.5 bg-border relative -mt-7">
                                        <div className="h-full bg-primary transition-all" style={{ width: `${lineProgress(i) * 100}%` }} />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            <div className="border-t border-border mt-4 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                    <div className="text-sm font-semibold">{stage || 0}</div>
                    <div className="text-[11px] text-muted-foreground">Aligners completed</div>
                </div>
                <div>
                    <div className="text-sm font-semibold">{weeksActive || "—"} wks</div>
                    <div className="text-[11px] text-muted-foreground">Active treatment</div>
                </div>
                <div>
                    <div className="text-sm font-semibold">{checkInCount}</div>
                    <div className="text-[11px] text-muted-foreground">Check-ins submitted</div>
                </div>
                <div>
                    <div className="text-sm font-semibold">{progress?.encouragement?.split(/[!.,]/)[0] || "Tracking"}</div>
                    <div className="text-[11px] text-muted-foreground">Treatment status</div>
                </div>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Doctor's latest note — pulls the most recent nudge addressed to this
// patient (`/patient/me/aligner-progress` returns the last 3).
// ────────────────────────────────────────────────────────────────────
function DoctorNoteCard({ progress, activeCase }) {
    const latest = progress?.nudges?.[0];
    if (!latest) return null;

    const doctorName = activeCase?.doctor_name || progress?.case?.doctor_name || "your doctor";
    const doctorImage = activeCase?.doctor_image || progress?.case?.doctor_image;

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-3" data-testid="doctor-note-card">
            <SectionHeader
                title={`Latest clinical alert from ${doctorName.startsWith("Dr") ? doctorName : `Dr. ${doctorName}`}`}
                link="/patient/my-case"
                linkLabel="See all"
            />

            <div className="flex items-center gap-2.5 mb-3">
                <DoctorAvatar
                    src={doctorImage}
                    name={doctorName}
                    size={32}
                    initialsClassName="bg-primary/10 border border-primary/30 text-primary text-xs font-bold"
                />
                <div>
                    <div className="text-xs font-medium">{doctorName.startsWith("Dr") ? doctorName : `Dr. ${doctorName}`}</div>
                    <div className="text-[11px] text-muted-foreground">{fmtRelative(latest.sent_at)} · {fmtDate(latest.sent_at)}</div>
                </div>
            </div>

            <div className="text-sm leading-relaxed text-foreground/85 bg-secondary/50 border-l-[3px] border-primary/60 rounded-lg p-3.5">
                {latest.message}
            </div>

            {/* iter98.19 — removed "Reply" link + button. Clinical alerts
                are one-way nudges; replying landed the patient in the
                generic doctor chat with no reference to the alert. For
                actual back-and-forth, the sidebar "Chat with doctor"
                entry remains the canonical path. */}
            <div className="mt-3 text-[11px] text-muted-foreground italic">
                Clinical alerts are one-way reminders from your doctor. To start a conversation, open <Link to="/patient/clinic-chat" className="text-primary hover:underline font-medium not-italic">Chat with doctor</Link>.
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Main dashboard
// ────────────────────────────────────────────────────────────────────
export default function PatientDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [cases, setCases] = useState([]);
    const [progress, setProgress] = useState(null);
    const [checkIns, setCheckIns] = useState({ check_ins: [], this_week_done: false });
    /* iter98.40 — Smile-goals first-visit onboarding gate.
       Open only when:
         (a) the GET returned null (= patient never completed the survey), AND
         (b) sessionStorage hasn't recorded a "dismissed this session" flag
             (so closing the modal doesn't auto-reopen on Dashboard re-renders),
       AND we've finished the initial GET (so the modal doesn't flash open
       and immediately close on a slow network). */
    const [smileGoalsOpen, setSmileGoalsOpen] = useState(false);
    const [smileGoals, setSmileGoals] = useState(undefined);  // undefined = not yet fetched

    const load = useCallback(() => {
        api.get("/appointments/my").then((r) => setAppointments(r.data)).catch(() => null);
        api.get("/cases/mine").then((r) => setCases(r.data)).catch(() => null);
        api.get("/patient/me/aligner-progress").then((r) => setProgress(r.data)).catch(() => null);
        api.get("/patient/me/check-ins").then((r) => setCheckIns(r.data)).catch(() => null);
        api.get("/patient/me/smile-goals").then((r) => setSmileGoals(r.data)).catch(() => setSmileGoals(null));
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // Only consider firing once the GET resolves.
        if (smileGoals === undefined) return;
        // Already completed → never auto-open.
        if (smileGoals && smileGoals.completed_at) return;
        // Dismissed this session → respect that until next sign-in.
        try {
            if (window.sessionStorage.getItem("invisique_smile_goals_dismissed") === "1") return;
        } catch { /* noop */ }
        setSmileGoalsOpen(true);
    }, [smileGoals]);

    const activeCase = useMemo(() => cases.find((c) => c.status !== "cancelled") || null, [cases]);
    const needsProfile = !user?.name || !user.name.trim();
    const isActivated = !!activeCase?.treatment_activated_at;

    const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const firstName = (user?.name || "").trim().split(/\s+/)[0] || "there";

    return (
        <div className="max-w-4xl mx-auto" data-testid="patient-dashboard">
            {/* iter98.40 — Smile-goals first-visit onboarding (single shot
                per session; closes silently to a Dashboard prompt card). */}
            <SmileGoalsOnboarding
                open={smileGoalsOpen}
                onClose={(completed) => {
                    setSmileGoalsOpen(false);
                    try { window.sessionStorage.setItem("invisique_smile_goals_dismissed", "1"); } catch { /* noop */ }
                    // Re-fetch so the local copy reflects the new state
                    // (drives the "share your smile goals" card below).
                    api.get("/patient/me/smile-goals").then((r) => setSmileGoals(r.data)).catch(() => null);
                }}
            />

            {needsProfile && <WelcomeCompletionCard />}

            {/* Compact top greeting (sits flush with the surrounding portal
                chrome — no second header bar needed since the layout shell
                already has one). */}
            <div className="mb-4">
                <div className="text-sm font-medium">{greeting()}, {firstName} 👋</div>
                <div className="text-xs text-muted-foreground">{today}</div>
            </div>

            {/* iter98.40 — Soft prompt if the patient skipped the survey.
                Lets them re-open the onboarding from the Dashboard without
                having to dig into the Profile page. Hidden once completed. */}
            {smileGoals !== undefined && !smileGoals?.completed_at && !smileGoalsOpen && (
                <SmileGoalsPromptCard onOpen={() => setSmileGoalsOpen(true)} />
            )}

            <HeroNextStep activeCase={activeCase} progress={progress} />

            {/* iter98.99f3 — CoE compliance coach banner. Renders only
                when the patient has an active assigned coach with a live
                thread; the banner itself returns null otherwise so we
                don't pollute the dashboard for non-opted-in patients. */}
            <div className="mb-3">
                <CoachBanner navigate={navigate} />
            </div>

            {/* Treatment dashboard body — gated on doctor activation */}
            {isActivated ? (
                <>
                    <TodayTasksStrip progress={progress} checkIns={checkIns} />
                    <div className="grid lg:grid-cols-2 gap-3 mb-3">
                        <WearTimeCard progress={progress} onReload={load} />
                        <NextSwitchCard progress={progress} activeCase={activeCase} />
                    </div>
                    <SundayCheckInCard activeCase={activeCase} checkIns={checkIns} onReload={load} />
                    <SmileJourneyStrip activeCase={activeCase} progress={progress} checkIns={checkIns} />
                    <DoctorNoteCard progress={progress} activeCase={activeCase} />
                </>
            ) : (
                /* Pre-activation: hero already gives context; the
                   TreatmentLockedCard explains what happens next. */
                activeCase && <TreatmentLockedCard />
            )}
        </div>
    );
}

function SmileGoalsPromptCard({ onOpen }) {
    return (
        <div
            className="mb-4 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-fuchsia-500/5 p-4 flex items-center gap-3"
            data-testid="smile-goals-prompt-card"
        >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Sparkle size={18} weight="fill" className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Tell us about your smile goals</div>
                <div className="text-xs text-muted-foreground mt-0.5">30 seconds — helps your doctor walk into the first visit prepared.</div>
            </div>
            <Button
                size="sm"
                onClick={onOpen}
                className="rounded-full shrink-0"
                data-testid="smile-goals-prompt-open-btn"
            >
                Start <ArrowRight size={14} className="ml-1" />
            </Button>
        </div>
    );
}
